import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { HTMLAttributes } from 'react'
import ArrowLeft from 'assets/icons/arrow-left.svg'
import { media } from 'styles/responsive'
import { themeUtils } from 'styles/theme'

export const CHECKOUT_BORDER_STYLE = `1px solid rgba(40, 37, 59, 0.06);`

interface DisableableContainerProps {
  disabled?: boolean
}
export const DisableableContainer = styled.div<DisableableContainerProps>`
  width: 100%;
  height: 100%;
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`

export const BlockContainer = styled.div`
  width: 100%;
`

interface FlexContainerProps {
  fullWidth?: boolean
}

interface FlexProps {
  justifyContent?:
    | 'center'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'baseLine'
  alignContent?:
    | 'center'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'stretch'
  flexDirection?: 'row' | 'column'
  flexWrap?: 'wrap' | 'nowrap'
  gap?: string
  margin?: string
  padding?: string
  flex?: string
  height?: string
  width?: string
  flexGrow?: number
  textAlign?: 'left' | 'center' | 'right'
}

export type NewFlexContainerProps = FlexProps & HTMLAttributes<HTMLDivElement>

export const FlexContainer = styled.div<FlexContainerProps>`
  display: flex;
  flex-grow: 1;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`
export const ColumnFlexContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const WithMarginAndPaddingContainer = styled.div<FlexProps>`
  padding: ${({ padding }) => padding ?? '0'};
  margin: ${({ margin }) => margin ?? '0'};
`

export const NewFlexContainer = styled(
  WithMarginAndPaddingContainer
)<FlexProps>`
  display: flex;
  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `}
  ${({ justifyContent }) =>
    justifyContent &&
    css`
      justify-content: ${justifyContent};
    `}
  ${({ alignItems }) =>
    alignItems &&
    css`
      align-items: ${alignItems};
    `}
  ${({ alignContent }) =>
    alignContent &&
    css`
      align-content: ${alignContent};
    `}
  ${({ flexDirection }) =>
    flexDirection &&
    css`
      flex-direction: ${flexDirection};
    `}
  ${({ flexWrap }) =>
    flexWrap &&
    css`
      flex-wrap: ${flexWrap};
    `}
  ${({ gap }) =>
    gap &&
    css`
      gap: ${gap};
    `}
${({ height }) =>
    height &&
    css`
      height: ${height};
    `}
${({ width }) =>
    width &&
    css`
      width: ${width};
    `}
  ${({ flex }) =>
    flex &&
    css`
      flex: ${flex};
    `}
  ${({ flexGrow }) =>
    flexGrow &&
    css`
      flex-grow: ${flexGrow};
    `}
`

interface GridContainerProps {
  gridTemplateColumns?: string
  gridTemplateRows?: string
}

export const NewGridContainer = styled(NewFlexContainer)<GridContainerProps>`
  display: grid;
  ${({ gridTemplateColumns }) =>
    gridTemplateColumns &&
    css`
      grid-template-columns: ${gridTemplateColumns};
    `}
  ${({ gridTemplateRows }) =>
    gridTemplateRows &&
    css`
      grid-template-rows: ${gridTemplateRows};
    `}
`

export const FlexContainerNoGrow = styled.div`
  display: flex;
`
export const FlexEndContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`
export const FlexCenteredContainer = styled(FlexContainer)`
  justify-content: center;
`

export const FlexCenteredContainerWithHeight = styled(FlexCenteredContainer)`
  height: 100vh;
  padding-top: 100px;
  width: 100%;
  justify-content: center;
`

interface VerticallySelfCenteredDivProps {
  fullWidth?: boolean
}
export const VerticallySelfCenteredDiv = styled.div<VerticallySelfCenteredDivProps>`
  align-self: center;
  min-width: 0;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`

interface FlexSpaceBetweenContainerProps {
  withMarginTop?: boolean
  withWrap?: boolean
  centerAlignItems?: boolean
}
export const FlexSpaceBetweenContainer = styled(
  FlexContainer
)<FlexSpaceBetweenContainerProps>`
  justify-content: space-between;
  min-width: 0;
  gap: 16px;
  ${({ withMarginTop }) =>
    withMarginTop &&
    css`
      margin-top: 24px;
    `}
  ${({ withWrap }) =>
    withWrap &&
    css`
      flex-wrap: wrap;
    `}
    ${({ centerAlignItems }) =>
    centerAlignItems &&
    css`
      align-items: center;
    `}
`

export const FlexSpaceAroundContainer = styled(FlexContainer)`
  justify-content: space-around;
`

interface TextProps {
  noMargin?: boolean
  italic?: boolean
  semiBold?: boolean
  bold?: boolean
  ellipsis?: boolean
  small?: boolean
  noLineHeight?: boolean
  noWrap?: boolean
  isTitle?: boolean
}
export const GreyText = styled.div<TextProps>`
  ${themeUtils.bodySmallGrey}
  ${({ ellipsis }) =>
    ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    `}
  ${({ bold }) =>
    bold &&
    css`
      ${themeUtils.bodySmallBoldGrey}
    `}  
  ${({ semiBold }) =>
    semiBold &&
    css`
      ${themeUtils.bodySmallSemiBoldGrey}
    `}
  ${({ noMargin }) =>
    !noMargin &&
    css`
      margin-bottom: 6px;
    `}
  ${({ italic }) =>
    italic &&
    css`
      font-style: italic;
    `}
    ${({ small }) =>
    small &&
    css`
      ${themeUtils.bodyXSmallGrey}
    `}
  ${({ noLineHeight }) =>
    noLineHeight &&
    css`
      line-height: inherit;
    `}
`
export const BlackText = styled.div<TextProps>`
  ${themeUtils.bodySmall}
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}
  ${({ ellipsis }) =>
    ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    `}
  ${({ bold }) =>
    bold &&
    css`
      ${themeUtils.bodyBold}
    `}
  ${({ semiBold }) =>
    semiBold &&
    css`
      ${themeUtils.bodySmallSemiBold}
    `}
  ${({ noMargin }) =>
    !noMargin &&
    css`
      margin-bottom: 6px;
    `}
  ${({ italic }) =>
    italic &&
    css`
      font-style: italic;
    `}
  ${({ noLineHeight }) =>
    noLineHeight &&
    css`
      line-height: inherit;
    `}
  ${({ isTitle }) =>
    isTitle &&
    css`
      ${themeUtils.h6}
    `}
`

interface DeviceSpecificContainerProps {
  fullWidth?: boolean
  withMarginTop?: boolean
  withGap?: boolean
  ellipsis?: boolean
}

// Container that hides everything inside of it if we are not on a mobile device
export const MobileContainer = styled.div<DeviceSpecificContainerProps>`
  ${media.minWidth.md} {
    display: none;
  }
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${({ withMarginTop }) =>
    withMarginTop &&
    css`
      margin-top: 12px;
    `}
    ${({ withGap }) =>
    withGap &&
    css`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `}
    ${({ ellipsis }) =>
    ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`
// Container that hides everything inside of it if we are not on a desktop device
export const DesktopContainer = styled.div<DeviceSpecificContainerProps>`
  ${media.maxWidth.md} {
    display: none;
  }
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${({ withMarginTop }) =>
    withMarginTop &&
    css`
      margin-top: 12px;
    `}
  ${({ ellipsis }) =>
    ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`

export const RelativeContainer = styled.div`
  position: relative;
  width: fit-content;
`

export const StyledBackButton = styled(ArrowLeft)`
  margin-right: 1%;
  margin-top: 3px;
  height: 30px;
  width: 30px;
  ${media.maxWidth.sm} {
    height: 24px;
    width: 24px;
    min-width: 24px;
    margin-right: 16px;
  }
  cursor: pointer;
`

interface GridContainerProps {
  gridTemplateColumns?: string
  gridTemplateRows?: string
}
export const GridContainer = styled.div<GridContainerProps>`
  display: grid;
  ${({ gridTemplateColumns }) =>
    gridTemplateColumns &&
    css`
      grid-template-columns: ${gridTemplateColumns};
    `}
  ${({ gridTemplateRows }) =>
    gridTemplateRows &&
    css`
      grid-template-rows: ${gridTemplateRows};
    `}
`
